import { SyntheticEvent } from 'react'
import SanityBlockContent from '@sanity/block-content-to-react'
import { renderToString } from 'katex'
import dynamic from 'next/dynamic'

import 'katex/dist/katex.min.css'

import { KATEX_MACROS } from '~/shared/constants/katex.const'
import MathVaultAwsImage from '~/components/MathVaultAwsImage'
import { getFileUrlWithoutEncoding } from '~/utils/cdnHelpers'
import BootcampAwsImage from '~/bootcamp/components/BootcampAwsImage'
import { getVideoTypeFromUrl, getFormattedVideoUrls, getFormattedVideoTracks } from '~/utils'
import { getUsableVideoDescription } from '~/curriculum/utils/videoPoster.utils'
import InteractiveShapeViewer from '~/bootcamp/Activities/Geometry/InteractiveShapeViewer'

import { GameChestContainer } from './GameChestContainer'
import Clock from './Clock/Clock'
import DigitalClock from './Clock/DigitalClock'
import DigitalText from './DigitalText'
import ResponsiveImage from './Image/ResponsiveImage'
import SanityImage from './Image/SanityImage'
import { SanityTable } from './SanityTable'

const BootcampVideo = dynamic(() => import('./BootcampVideo'))

interface Props<T> {
  node: T
}

interface ChestNodeProps {
  _key: string
  _type: 'chest'
  event: {
    _type: 'event'
    points: number
  }
  funfact?: unknown[]
}

// Currently @sanity/block-content-to-react doesn't support passing data from the renderer component.
// Thus a global variable is being used to persist the function reference otherwise unnecessary re-render happens and bootcamp video doesn't play.
// TODO: @sanity/block-content-to-react is deprecated. Migrate to https://github.com/portabletext/react-portabletext
let bootcampVideoPlayCallback: any = undefined
let bootcampVideoEndCallback: any = undefined

const Serializers = {
  types: {
    // eslint-disable-next-line react/display-name
    latex: (props: any) => {
      const text = props.node.body ? props.node.body : props.node?.value?.body
      const html = {
        __html: renderToString(text ? text : '', {
          macros: { ...KATEX_MACROS },
          throwOnError: false,
          output: 'html',
        }),
      }
      // isInline has a bug, for list item.
      // katex items inside li are detected as katex-block regardless of them being block or inline items according to isInline property
      return <span className={props.isInline ? 'katex-inline' : 'katex-block'} dangerouslySetInnerHTML={html} />

      // return props.isInline ? <span dangerouslySetInnerHTML={html} /> : <p dangerouslySetInnerHTML={html} />
    },

    // eslint-disable-next-line react/display-name
    image: (props: any) => {
      return <SanityImage alt={props.node.alt} asset={props.node.asset} maxWidth={props.node?.maxWidth} />
    },

    // eslint-disable-next-line react/display-name
    clock: (props: any) => {
      const { hours, minutes, digital, version } = props.node
      if (digital) {
        return (
          <div className="relative h-full w-full grid place-content-center ">
            <DigitalClock className="w-28" hours={hours} minutes={minutes} version={version} />
          </div>
        )
      }

      return (
        <div className="relative h-full w-full flex justify-center">
          <Clock className="min-h-20 min-w-20" hours={hours} minutes={minutes} />
        </div>
      )
    },

    digitalText: (props: any) => {
      const { content } = props.node
      return <DigitalText content={content} />
    },

    // eslint-disable-next-line react/display-name
    awsImage: (props: any) => {
      return (
        <ResponsiveImage
          alt={props.node.alt}
          className="block mx-auto max-w-full"
          height={props.height}
          loading="eager"
          srcKey={props.node.srcKey}
          width={props.width}
        />
      )
    },

    mathVaultAwsImage: MathVaultAwsImage,

    bootcampAwsImage: BootcampAwsImage,

    bootcampAwsImageSimplified: BootcampAwsImage,

    // eslint-disable-next-line react/display-name
    chest: (props: Props<ChestNodeProps>) => {
      const { event, funfact, _key } = props.node
      return <GameChestContainer event={event} eventId={_key} funfact={funfact} />
    },

    awsVideo: (props: any) => {
      const { srcKey, hideControls, loopVideo, autoPlay, videoUrls, videoTracks, description } = props.node
      return (
        <BootcampVideo
          autoplay={autoPlay}
          loop={loopVideo}
          muted={autoPlay}
          onEnd={bootcampVideoEndCallback}
          onPlay={bootcampVideoPlayCallback}
          posterTitle={getUsableVideoDescription(description)}
          showControls={!hideControls}
          src={getFileUrlWithoutEncoding(srcKey)}
          srcType={getVideoTypeFromUrl(srcKey)}
          videoTracks={getFormattedVideoTracks(videoTracks)}
          videoUrls={getFormattedVideoUrls(videoUrls)}
        />
      )
    },

    awsBootcampActivityVideo: (props: any) => {
      const { srcKey, srcKeyFallbackVideo, hideControls, loopVideo, autoPlay, description } = props.node
      return (
        <BootcampVideo
          autoplay={autoPlay}
          fallbackSrc={srcKeyFallbackVideo ? getFileUrlWithoutEncoding(srcKeyFallbackVideo) : undefined}
          fallbackSrcType={getVideoTypeFromUrl(srcKeyFallbackVideo)}
          loop={loopVideo}
          muted={autoPlay}
          posterTitle={getUsableVideoDescription(description)}
          showControls={!hideControls}
          src={getFileUrlWithoutEncoding(srcKey)}
          srcType={getVideoTypeFromUrl(srcKey)}
        />
      )
    },

    interactiveShapeViewer: (props: { node: { shape: string } }) => {
      return <InteractiveShapeViewer shape={props.node.shape} />
    },

    table: SanityTable,
  },

  marks: {
    sup: ({ children }: { children: string[] }) => {
      return <sup>{children}</sup>
    },
  },

  text: ({ children }: { children: string }) => {
    if (typeof children === 'string' && children) {
      const newWord = children.replace(/[\s]{2}/g, ' &nbsp;')
      return <span dangerouslySetInnerHTML={{ __html: newWord }} />
    }
    return <>{children}</>
  },
}

export interface BlockContentProps {
  blocks: unknown[]
  className?: string
  renderContainerOnSingleChild?: boolean
  onBootcampVideoPlay?: (e: SyntheticEvent<HTMLVideoElement, Event>) => void
  onBootcampVideoEnd?: () => void
}

export function BlockContent({
  blocks,
  className,
  renderContainerOnSingleChild,
  onBootcampVideoPlay,
  onBootcampVideoEnd,
}: BlockContentProps): React.ReactElement {
  bootcampVideoPlayCallback = onBootcampVideoPlay
  bootcampVideoEndCallback = onBootcampVideoEnd

  return (
    <SanityBlockContent
      blocks={blocks}
      className={className}
      renderContainerOnSingleChild={renderContainerOnSingleChild}
      serializers={Serializers}
    />
  )
}
